import React, { useState } from 'react';
import { formatDateDisplay, getQuickDateOptions } from '../../../../utils/dateUtils';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { taskV2ActionsAtom } from '../../../../atoms/v3/taskV2Atoms';
import { isDarkAtom } from '../../../../atoms/shared/uiAtoms';
import { forceRefreshProjectsAtom, projectsLoadingAtom } from '../../../../atoms/v0/projectAtoms';
import { forceRefreshTasksAtom, tasksLoadingAtom } from '../../../../atoms/v0/taskAtoms';
import { layoutModeAtom } from '../../../../atoms/v3/uiAtoms';
import { isTouchDevice } from '../../../../utils/platformUtils';
import { DateSelector } from './DateSelector';
import { Task } from '../../../../types/task';
import { IOSHeaderContainer } from '../ios/IOSHeaderContainer';

interface TaskV2HeaderProps {
  currentDate: Date;
  viewMode: 'list' | 'timeline' | 'quadrant';
  showNoTimeTasks: boolean;
  specialMode: 'past' | 'future' | 'notime' | 'all' | 'recent' | null;
  allTasks: Task[]; // 完整的任务列表，用于日历密度计算
  onDateChange: (date: Date) => void;
  onViewModeChange: (mode: 'list' | 'timeline' | 'quadrant') => void;
  onShowNoTimeTasksChange: (show: boolean) => void;
  onSpecialModeChange: (mode: 'past' | 'future' | 'notime' | 'all' | 'recent' | null) => void;
}

/**
 * TaskV2 顶部导航组件
 */
export const TaskV2Header: React.FC<TaskV2HeaderProps> = ({
  currentDate,
  viewMode,
  showNoTimeTasks,
  specialMode,
  allTasks,
  onDateChange,
  onViewModeChange,
  onShowNoTimeTasksChange,
  onSpecialModeChange
}) => {
  const { t, i18n } = useTranslation(['v3/tasks', 'v3/common']);
  const navigate = useNavigate();
  const [isDateSelectorOpen, setIsDateSelectorOpen] = useState(false);
  const [isDark] = useAtom(isDarkAtom);
  const layoutMode = useAtomValue(layoutModeAtom);

  // 使用统一的状态管理
  const getActions = useSetAtom(taskV2ActionsAtom);
  const actions = getActions();

  // 刷新相关状态
  const [, forceRefreshProjects] = useAtom(forceRefreshProjectsAtom);
  const [, forceRefreshTasks] = useAtom(forceRefreshTasksAtom);
  const [projectsLoading] = useAtom(projectsLoadingAtom);
  const [tasksLoading] = useAtom(tasksLoadingAtom);
  const isRefreshing = projectsLoading || tasksLoading;

  const dateOptions = getQuickDateOptions(currentDate);

  // 处理刷新操作
  const handleRefresh = async () => {
    if (isRefreshing) return;

    try {
      // 同时刷新项目和任务数据
      await Promise.all([
        forceRefreshProjects(),
        forceRefreshTasks()
      ]);
    } catch (error) {
      console.error('刷新数据失败:', error);
    }
  };

  // 判断是否可以显示时间轴模式 - 只有具体日期才可以，特殊模式不可以
  const canShowTimeline = !specialMode;

  // 获取显示文本 - 特殊模式下显示对应文本，否则显示日期
  const getDisplayText = () => {
    if (specialMode === 'past') return t('dateSelector.pastAll');
    if (specialMode === 'future') return t('dateSelector.futureAll');
    if (specialMode === 'notime') return t('dateSelector.noTime');
    if (specialMode === 'all') return t('dateSelector.allTasks');
    if (specialMode === 'recent') return t('dateSelector.recent');
    return formatDateDisplay(currentDate, i18n.language);
  };

  // 处理日期点击 - 切换日历选择器
  const handleDateClick = () => {
    setIsDateSelectorOpen(!isDateSelectorOpen);
  };

  // 处理左箭头（前一天）
  const handlePrevDay = () => {
    setIsDateSelectorOpen(false); // 自动折叠
    actions.goToPrevDay();
  };

  // 处理右箭头（后一天）
  const handleNextDay = () => {
    setIsDateSelectorOpen(false); // 自动折叠
    actions.goToNextDay();
  };

  // 处理时间轴模式切换
  const handleViewModeToggle = () => {
    setIsDateSelectorOpen(false); // 自动折叠
    actions.toggleViewMode();
  };

  // 处理回到今天
  const handleGoToToday = () => {
    setIsDateSelectorOpen(false); // 自动折叠
    actions.goToToday();
  };

  // 获取当前视图模式的文本
  const getCurrentViewModeText = () => {
    switch (viewMode) {
      case 'list':
        return t('v3/tasks:viewMode.list');
      case 'timeline':
        return t('v3/tasks:viewMode.timeline');
      case 'quadrant':
        return t('v3/tasks:viewMode.quadrant');
      default:
        return '';
    }
  };

  // 获取当前视图模式的图标
  const getCurrentViewModeIcon = () => {
    switch (viewMode) {
      case 'list':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        );

      case 'timeline':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'quadrant':
        return (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
          </svg>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <IOSHeaderContainer mode={layoutMode === 'desktop' ? 'desktop-main' : 'mobile'}>
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            {/* 左侧：日期导航 */}
            <div className="flex items-center space-x-2">
              {/* 旧版日历入口 */}
              <button
                onClick={() => navigate('/v3/calendar')}
                className={`
                  p-1 rounded-full transition-colors
                  ${isDark
                    ? 'hover:bg-ios-dark-gray4 text-ios-dark-gray1'
                    : 'hover:bg-gray-100 text-gray-600'
                  }
                `}
                title="旧版日历"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </button>

              {/* 左箭头 */}
              <button
                onClick={specialMode ? undefined : handlePrevDay}
                disabled={!!specialMode}
                className={`
                  p-1 rounded-full transition-colors
                  ${specialMode
                    ? 'cursor-not-allowed opacity-50'
                    : isDark
                      ? 'hover:bg-ios-dark-gray4 cursor-pointer'
                      : 'hover:bg-gray-100 cursor-pointer'
                  }
                `}
              >
                <svg
                  className={`w-5 h-5 ${
                    specialMode
                      ? isDark ? 'text-ios-dark-gray3' : 'text-gray-400'
                      : isDark ? 'text-ios-dark-gray1' : 'text-gray-600'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              {/* 日期显示 */}
              <button
                onClick={handleDateClick}
                className={`
                  px-3 py-1 rounded-lg transition-colors min-w-[140px] text-left
                  ${isDateSelectorOpen
                    ? isDark
                      ? 'bg-blue-900/30 text-blue-400'
                      : 'bg-blue-100 text-blue-600'
                    : isDark
                      ? 'bg-ios-dark-gray4 hover:bg-ios-dark-gray3'
                      : 'bg-gray-100 hover:bg-gray-200'
                  }
                `}
              >
                {/* 日期显示：统一的一大一小格式 */}
                {specialMode ? (
                  <span className={`text-lg font-semibold ${isDark ? 'text-ios-dark-gray1' : 'text-gray-900'}`}>
                    {getDisplayText()}
                  </span>
                ) : dateOptions.isToday || dateOptions.isTomorrow || dateOptions.isYesterday ? (
                  <>
                    <span className={`text-lg font-semibold ${isDark ? 'text-ios-dark-gray1' : 'text-gray-900'}`}>
                      {getDisplayText()} {/* Today/Tomorrow/Yesterday */}
                    </span>
                    <span className={`text-sm ml-1 ${isDark ? 'text-ios-dark-gray2' : 'text-gray-500'}`}>
                      {currentDate.toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : 'en-US', {
                        month: i18n.language === 'zh' ? 'long' : 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </>
                ) : (
                  /* 非特殊日期：大字显示星期，小字显示月日 */
                  <>
                    <span className={`text-lg font-semibold ${isDark ? 'text-ios-dark-gray1' : 'text-gray-900'}`}>
                      {currentDate.toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : 'en-US', {
                        weekday: 'short'
                      })}
                    </span>
                    <span className={`text-sm ml-1 ${isDark ? 'text-ios-dark-gray2' : 'text-gray-500'}`}>
                      {currentDate.toLocaleDateString(i18n.language === 'zh' ? 'zh-CN' : 'en-US', {
                        month: i18n.language === 'zh' ? 'long' : 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </>
                )}
              </button>

              {/* 右箭头 */}
              <button
                onClick={specialMode ? undefined : handleNextDay}
                disabled={!!specialMode}
                className={`
                  p-1 rounded-full transition-colors
                  ${specialMode
                    ? 'cursor-not-allowed opacity-50'
                    : isDark
                      ? 'hover:bg-ios-dark-gray4 cursor-pointer'
                      : 'hover:bg-gray-100 cursor-pointer'
                  }
                `}
              >
                <svg
                  className={`w-5 h-5 ${
                    specialMode
                      ? isDark ? 'text-ios-dark-gray3' : 'text-gray-400'
                      : isDark ? 'text-ios-dark-gray1' : 'text-gray-600'
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              {/* 快速回到今天图标 - 在特殊模式下或不是今天时显示 */}
              {(specialMode || !dateOptions.isToday) && (
                <button
                  onClick={handleGoToToday}
                  className={`p-1 rounded-full transition-colors ml-1 ${
                    isDark
                      ? 'hover:bg-blue-900/30'
                      : 'hover:bg-blue-50'
                  }`}
                  title={specialMode ? "回到今天" : "回到今天"}
                >
                  <svg
                    className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    <circle cx="12" cy="12" r="2" fill="currentColor" />
                  </svg>
                </button>
              )}
            </div>

            {/* 右侧：功能按钮 */}
            <div className="flex items-center space-x-2">
              {/* 刷新按钮 */}
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className={`
                  p-2 rounded-lg transition-colors
                  ${isRefreshing
                    ? 'opacity-50 cursor-not-allowed'
                    : isDark
                      ? 'hover:bg-ios-dark-gray4 text-ios-dark-gray2'
                      : 'hover:bg-gray-100 text-gray-500'
                  }
                `}
                title={t('common:refresh.refreshButton')}
              >
                <svg
                  className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </button>



              {/* 视图模式切换按钮 */}
              <button
                onClick={handleViewModeToggle}
                className={`
                  flex items-center gap-2 px-3 py-1.5 rounded-lg transition-all duration-200
                  ${isDark
                    ? 'bg-ios-dark-gray5 text-ios-dark-gray2 hover:bg-ios-dark-gray4'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }
                `}
              >
                {getCurrentViewModeIcon()}
                <span className="text-sm font-medium">
                  {getCurrentViewModeText()}
                </span>
              </button>
            </div>
          </div>
        </div>
      </IOSHeaderContainer>

      {/* 日历选择器 - 放在 IOSHeaderContainer 外部，避免被遮挡 */}
      <DateSelector
        isOpen={isDateSelectorOpen}
        onClose={() => setIsDateSelectorOpen(false)}
        currentDate={currentDate}
        specialMode={specialMode}
        onDateChange={(date) => {
          onDateChange(date);
          setIsDateSelectorOpen(false);
        }}
        onSpecialModeChange={onSpecialModeChange}
        tasks={allTasks}
      />


    </>
  );
};
